import React, { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { isNonEmptyArray } from "../../../../../../common/utils";
import styles from "./ContentLayer.module.css";
import { CustomButton, icon } from "../../../../../../common/components";
import { CollabService } from "../../../../../../api";
import { useApi, useTrackEvent } from "../../../../../../common/hooks";
import { translate } from "../../../../../../common/providers";
import { setHoveredInformation } from "../../../../slice/notes-pdf/notes-pdf-slice";

const ContentLayer = ({ editor, overlay, height, width }) => {
	const { call: getStringContent } = useApi(CollabService.getStringContent);
	const document = useSelector(({ notesSelectedDocument }) => notesSelectedDocument.selectedDocument);
	const hoveredInformation = useSelector(({ notesPdf }) => notesPdf.hoveredInformation);
	const selection = useSelector(({ notesPdf }) => notesPdf.selection);
	const tooltipRef = useRef();
	const stableHighlightMap = useRef({});
	const dispatch = useDispatch();
	const trackEvent = useTrackEvent();
	const insertSingularContent = (info) => {
		getStringContent({ infoId: info.informationId })
			.then((data) => {
				const { informationId, pageStart, contentType } = info;
				const isClause = contentType === "CLAUSE";
				editor
					.chain()
					.focus()
					.insertContent([
						{ type: "paragraph" },
						{
							type: isClause ? "lineReference" : "contentBox",
							attrs: (isClause && {
								content: data.value,
								documentId: document?.id,
								itemId: informationId,
								refType: "INFORMATION",
							}) || {
								informations: [
									{
										content: data.value,
										documentId: document?.id,
										documentName: document?.name,
										itemId: informationId,
										pageStart,
										refType: "INFORMATION",
									},
								],
							},
						},
						{ type: "paragraph" },
					])
					.run();
				trackEvent(isClause ? "Notes_Insert_Chapter_Reference" : "Notes_Insert_Content_Reference");
			})
			.catch(console.error);
	};
	const insertMultipleContent = async () => {
		try {
			const contentsToInsert = await Promise.all(
				selection?.ids.map(async (info) => {
					const data = await getStringContent({ infoId: info.informationId });
					const { informationId, pageStart } = info;
					return {
						content: data.value,
						documentId: document?.id,
						documentName: document?.name,
						itemId: informationId,
						pageStart,
						refType: "INFORMATION",
					};
				})
			);
			editor
				.chain()
				.focus()
				.insertContent([
					{ type: "paragraph" },
					{
						type: "contentBox",
						attrs: {
							informations: contentsToInsert,
						},
					},
					{ type: "paragraph" },
				])
				.run();
			trackEvent("Notes_Insert_Content_Reference");
		} catch (error) {
			console.error(error);
		}
	};
	const insertContentBox = (info, multiInsert = false, isHighlighted = false) => {
		if (!editor.isEditable || isHighlighted) {
			return;
		}
		if (isNonEmptyArray(selection?.ids) && multiInsert) {
			insertMultipleContent();
		} else {
			insertSingularContent(info);
		}
	};
	const handleMouseEnter = (informationId) => {
		dispatch(setHoveredInformation({ id: informationId, highlighted: false }));
	};
	const handleMouseLeave = (informationId) => {
		stableHighlightMap.current[informationId] = false;
	};
	const tooltipElement = (info, multiInsert, isHighlighted) => (
		<CustomButton
			className={styles.tooltipButton}
			disabled={!editor.isEditable}
			endIcon={icon.faArrowRight}
			size="sm"
			onClick={() => insertContentBox(info, multiInsert, isHighlighted)}
		>
			{translate("notes.insertion.insert-to-note")}
		</CustomButton>
	);
	return (
		<>
			{isNonEmptyArray(overlay?.informations) &&
				overlay.informations.map((info) => {
					const adjustedHeight = Math.abs(info.rectangle.y2 - info.rectangle.y1 + 0.007) * height + 8;
					const adjustedWidth = Math.abs(info.rectangle.x2 - info.rectangle.x1) * width + 8;
					const adjustedTop = (info.rectangle.y1 - 0.006) * height - 4;
					const adjustedLeft = info.rectangle.x1 * width - 4;
					const isHovered = hoveredInformation.id === info.informationId;
					const isInSelection = selection?.ids?.some((sel) => sel.informationId === info.informationId);
					const isHighlighted =
						(isHovered && hoveredInformation.highlighted) || (isInSelection && selection.highlighted);
					if (isHighlighted) {
						stableHighlightMap.current[info.informationId] = true;
					} else if (!hoveredInformation.id && !isInSelection) {
						stableHighlightMap.current[info.informationId] = false;
					}
					const stableHighlight = stableHighlightMap.current[info.informationId];
					return (
						<>
							{((isHovered && !selection.ids?.some((s) => s.informationId === info.informationId)) ||
								selection.ids?.[0]?.informationId === info.informationId) &&
								!stableHighlight && (
									<div
										ref={tooltipRef}
										className={styles.hoverBox}
										style={{
											top: adjustedTop - 20 < 0 ? 0 : adjustedTop - 20,
											left: adjustedLeft + adjustedWidth - tooltipRef.current?.offsetWidth,
										}}
									>
										{tooltipElement(info, isInSelection, isHighlighted)}
									</div>
								)}
							<div
								key={info.informationId}
								alt={info.informationId}
								className={styles.contentBox}
								data-hovered={isHovered}
								data-is-highlighted={isHighlighted}
								data-selected={isInSelection}
								role="presentation"
								style={{
									top: adjustedTop,
									left: adjustedLeft,
									height: adjustedHeight,
									width: adjustedWidth,
								}}
								onClick={() => insertContentBox(info, isInSelection, isHighlighted)}
								onMouseEnter={() => handleMouseEnter(info.informationId)}
								onMouseLeave={() => handleMouseLeave(info.informationId)}
							/>
						</>
					);
				})}
		</>
	);
};

export default ContentLayer;
